import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Trash2, Edit2, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmployeesTable = ({ employees, language, onDelete, onRefresh }) => (
  <div className="bg-white border border-stone-200 overflow-hidden">
    <div className="p-4 border-b border-stone-200 flex items-center justify-between">
      <h3 className="text-stone-900 font-semibold">{language === 'sv' ? 'Medarbetare' : 'Employees'}</h3>
      <EmployeeFormDialog onSuccess={onRefresh} language={language} />
    </div>
    <div className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow className="bg-stone-50"><TableHead>{language === 'sv' ? 'Bild' : 'Photo'}</TableHead><TableHead>{language === 'sv' ? 'Namn' : 'Name'}</TableHead><TableHead>Position</TableHead><TableHead>Email</TableHead><TableHead className="text-right">{language === 'sv' ? 'Åtgärder' : 'Actions'}</TableHead></TableRow></TableHeader>
        <TableBody>
          {employees.map((emp) => (
            <TableRow key={emp.id} className="hover:bg-stone-50">
              <TableCell>{emp.image_url ? <img src={emp.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${emp.image_url}` : emp.image_url} alt={emp.name} className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center"><Users className="w-5 h-5 text-stone-400" /></div>}</TableCell>
              <TableCell className="font-medium">{emp.name}</TableCell>
              <TableCell>{language === 'sv' ? emp.position_sv : emp.position_en}</TableCell>
              <TableCell>{emp.email}</TableCell>
              <TableCell className="text-right"><div className="flex justify-end gap-1"><EmployeeFormDialog onSuccess={onRefresh} language={language} editData={emp} /><Button variant="ghost" size="icon" onClick={() => onDelete(emp.id)} className="text-stone-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></Button></div></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </div>
);

const EmployeeFormDialog = ({ onSuccess, language, editData = null }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState('');
  const [position_sv, setPosSv] = useState('');
  const [position_en, setPosEn] = useState('');
  const [bio_sv, setBioSv] = useState('');
  const [bio_en, setBioEn] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [image_url, setImageUrl] = useState('');
  const [order, setOrder] = useState(0);

  useEffect(() => {
    if (editData && open) {
      setName(editData.name || ''); setPosSv(editData.position_sv || ''); setPosEn(editData.position_en || ''); setBioSv(editData.bio_sv || ''); setBioEn(editData.bio_en || ''); setEmail(editData.email || ''); setPhone(editData.phone || ''); setImageUrl(editData.image_url || ''); setOrder(editData.order || 0);
    } else if (open) {
      setName(''); setPosSv(''); setPosEn(''); setBioSv(''); setBioEn(''); setEmail(''); setPhone(''); setImageUrl(''); setOrder(0);
    }
  }, [editData, open]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload`, formData);
      setImageUrl(res.data.url);
      toast.success(language === 'sv' ? 'Bild uppladdad' : 'Image uploaded');
    } catch (error) {
      toast.error(language === 'sv' ? 'Fel vid uppladdning' : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = { name, position_sv, position_en, email, phone, image_url, order };
      if (editData) { await axios.put(`${API}/employees/${editData.id}`, data); toast.success(language === 'sv' ? 'Medarbetare uppdaterad' : 'Employee updated'); }
      else { await axios.post(`${API}/employees`, data); toast.success(language === 'sv' ? 'Medarbetare skapad' : 'Employee created'); }
      setOpen(false); onSuccess();
    } catch (error) { toast.error('Error'); } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{editData ? <Button variant="ghost" size="icon" className="text-stone-500 hover:text-stone-700"><Edit2 className="w-4 h-4" /></Button> : <Button className="bg-stone-900 text-white hover:bg-stone-800"><Plus className="w-4 h-4 mr-2" /> {language === 'sv' ? 'Ny medarbetare' : 'New employee'}</Button>}</DialogTrigger>
      <DialogContent className="bg-white border-stone-200 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editData ? (language === 'sv' ? 'Redigera medarbetare' : 'Edit employee') : (language === 'sv' ? 'Lägg till medarbetare' : 'Add employee')}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center gap-4">
            {image_url ? <img src={image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${image_url}` : image_url} alt="Preview" className="w-20 h-20 rounded-full object-cover border-2 border-stone-200" /> : <div className="w-20 h-20 rounded-full bg-stone-100 flex items-center justify-center border-2 border-stone-200"><Users className="w-8 h-8 text-stone-400" /></div>}
            <div className="flex-1"><Label>{language === 'sv' ? 'Profilbild' : 'Profile photo'}</Label><Input type="file" accept="image/*" onChange={handleImageUpload} className="bg-white border-stone-300" disabled={uploading} />{uploading && <p className="text-sm text-stone-500">{language === 'sv' ? 'Laddar upp...' : 'Uploading...'}</p>}</div>
          </div>
          <div><Label>Namn</Label><Input value={name} onChange={(e) => setName(e.target.value)} required className="bg-white border-stone-300" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Position (SV)</Label><Input value={position_sv} onChange={(e) => setPosSv(e.target.value)} required className="bg-white border-stone-300" /></div>
            <div><Label>Position (EN)</Label><Input value={position_en} onChange={(e) => setPosEn(e.target.value)} required className="bg-white border-stone-300" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-white border-stone-300" /></div>
            <div><Label>Telefon</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} required className="bg-white border-stone-300" /></div>
          </div>
          <div><Label>Ordning</Label><Input type="number" value={order} onChange={(e) => setOrder(parseInt(e.target.value) || 0)} className="bg-white border-stone-300" /></div>
          <DialogFooter><Button type="submit" disabled={submitting} className="bg-stone-900 text-white">{submitting ? '...' : (language === 'sv' ? 'Spara' : 'Save')}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeesTable;
export { EmployeeFormDialog };
